<template>
    <tab-layout :buttons="buttons" :permissions="permissionsToEdit" @edit="openEdit">
        <!-- TAB TITLE -->
        <template #title>
            {{ $l10n.__('Préférences') }}
        </template>

        <!-- TAB CONTENT -->
        <template #content>
            <!-- Edition Prefs form -->
            <EditPreferencesThird
                v-if="enableEdit && !metaLoading"
                :metadata="metadata"
                :preferences="preferences"
                @close="enableEdit = false"
                @saved="hasSaved"
            />

            <!-- Default Read Prefs -->
            <div v-else-if="!loading" class="well p-4 flex-level flex-top">
                <div class="wp-5">
                    <section class="mb-4">
                        <h3 class="mt-0 mb-2 lh-24 fs-4 bold">{{ $l10n.__('Informations') }}</h3>

                        <article class="mb-3">
                            <p class="fs-3 lh-20 gray-60 m-0">{{ $l10n.__('Devise') }}</p>
                            <p
                                v-if="!preferences.currency.id"
                                class="fs-3 lh-20 semibold mt-1 mb-0"
                            >
                                {{ $l10n.__('Aucune devise spécifiée') }}
                            </p>
                            <template v-else>
                                <p class="fs-3 lh-20 semibold mt-1 mb-0">
                                    {{ preferences.currency.symbol }}
                                </p>
                                <p class="fs-3 lh-20 semibold mt-1 mb-0">
                                    {{ preferences.currency.name }}
                                </p>
                            </template>
                        </article>
                        <article class="mb-3">
                            <p class="fs-3 lh-20 gray-60 m-0">{{ $l10n.__('Transporteur') }}</p>
                            <p
                                v-if="!preferences.shipping.id"
                                class="fs-3 lh-20 semibold mt-1 mb-0"
                            >
                                {{ $l10n.__('Aucun transporteur spécifié') }}
                            </p>
                            <template v-else>
                                <p class="fs-3 lh-20 semibold mt-1 mb-0">
                                    {{ preferences.shipping.name }}
                                </p>
                            </template>
                        </article>
                        <article class="mb-3">
                            <p class="fs-3 lh-20 gray-60 m-0">{{ $l10n.__('TVA par défaut') }}</p>
                            <p class="fs-3 lh-20 semibold mt-1 mb-0">
                                <span v-if="!!preferences.tax.label">
                                    {{ preferences.tax.label }} -
                                </span>
                                {{ preferences.tax.rate }}
                            </p>
                        </article>
                        <article class="mb-3">
                            <p class="fs-3 lh-20 gray-60 m-0">
                                {{ $l10n.__('Délai de règlement') }}
                            </p>
                            <p v-if="!metaLoading" class="fs-3 lh-20 semibold mt-1 mb-0">
                                <span v-if="currentPaymentTerm.code === 'xdays'" class="ml-1">
                                    {{ preferences.paymentTerm.xDays }}
                                    {{ $l10n.n__('jour', 'jours', preferences.paymentTerm.xDays) }}
                                </span>
                                <span v-else>
                                    {{ preferences.paymentTerm.label }}
                                </span>
                            </p>
                            <p
                                v-if="preferences.paymentTerm.endOfMonth"
                                class="fs-3 lh-20 semibold mt-3 mb-0"
                            >
                                {{ $l10n.__('En fin de mois') }}
                            </p>
                        </article>
                        <article class="mb-3">
                            <p class="fs-3 lh-20 gray-60 m-0">
                                {{ $l10n.__('Réduction globale') }}
                            </p>
                            <p class="fs-3 lh-20 semibold mt-1 mb-0">
                                {{ $filters.numberFormat(preferences.discount.amount) }}
                                <span v-if="preferences.discount.isAmountDiscount">€</span>
                                <span v-else-if="preferences.discount.isPercentDiscount">%</span>
                            </p>
                        </article>
                        <article>
                            <p class="fs-3 lh-20 gray-60 m-0">{{ $l10n.__('Compte bancaire') }}</p>
                            <p
                                v-if="!preferences.bankAccount.id"
                                class="fs-3 lh-20 semibold mt-1 mb-0"
                            >
                                {{ $l10n.__('Aucun compte bancaire spécifié') }}
                            </p>
                            <template v-else>
                                <p class="fs-3 lh-20 semibold mt-1 mb-0">
                                    {{ preferences.bankAccount.label }}
                                </p>
                                <p class="fs-3 lh-20 semibold mt-1 mb-0">
                                    {{ preferences.bankAccount.iban }}
                                </p>
                            </template>
                            <p
                                v-if="preferences.discountByRow"
                                class="fs-3 lh-20 semibold mt-3 mb-0"
                            >
                                {{ $l10n.__('Chaque ligne peut avoir sa remise spécifique') }}
                            </p>
                        </article>
                    </section>

                    <section v-if="!isSupplier && Object.keys(preferences.dunning).length">
                        <h3 class="mt-0 mb-2 lh-24 fs-4 bold">
                            {{ $l10n.__('Plan de relance') }}
                        </h3>
                        <article v-if="preferences.dunning.estimate" class="pref-dunning">
                            <p class="fs-3 lh-20 gray-60 m-0">{{ $l10n.__('Plan des Devis') }}</p>

                            <p class="fs-3 lh-20 semibold mt-1 mb-0">
                                <span v-if="preferences.dunning.estimate.value === 'main'">
                                    {{ $l10n.__('Utiliser le plan par défaut') }}
                                </span>
                                <span v-else-if="preferences.dunning.estimate.value === 'disabled'">
                                    {{ $l10n.__('Désactiver') }}
                                </span>
                                <span v-else>{{ preferences.dunning.estimate.label }}</span>
                            </p>
                        </article>
                        <article v-if="preferences.dunning.invoice" class="pref-dunning">
                            <p class="fs-3 lh-20 gray-60 m-0">
                                {{ $l10n.__('Plan des Factures') }}
                            </p>

                            <p class="fs-3 lh-20 semibold mt-1 mb-0">
                                <span v-if="preferences.dunning.invoice.value === 'main'">
                                    {{ $l10n.__('Utiliser le plan par défaut') }}
                                </span>
                                <span v-else-if="preferences.dunning.invoice.value === 'disabled'">
                                    {{ $l10n.__('Désactiver') }}
                                </span>
                                <span v-else>{{ preferences.dunning.invoice.label }}</span>
                            </p>
                        </article>
                        <article v-if="preferences.dunning.proforma" class="pref-dunning">
                            <p class="fs-3 lh-20 gray-60 m-0">
                                {{ $l10n.__('Plan des Proforma') }}
                            </p>

                            <p class="fs-3 lh-20 semibold mt-1 mb-0">
                                <span v-if="preferences.dunning.proforma.value === 'main'">
                                    {{ $l10n.__('Utiliser le plan par défaut') }}
                                </span>
                                <span v-else-if="preferences.dunning.proforma.value === 'disabled'">
                                    {{ $l10n.__('Désactiver') }}
                                </span>
                                <span v-else>{{ preferences.dunning.proforma.label }}</span>
                            </p>
                        </article>
                    </section>
                </div>
                <div class="wp-5">
                    <section>
                        <article v-if="!isSupplier" class="mb-3">
                            <p class="fs-3 lh-20 gray-60 m-0">
                                {{ $l10n.__('Catégorie tarifaire') }}
                            </p>
                            <p class="fs-3 lh-20 semibold mt-1 mb-0">
                                {{ preferences.rateCategory.label }}
                            </p>
                        </article>
                        <article>
                            <p class="fs-3 lh-20 gray-60 m-0">
                                {{ $l10n.__('Moyen de règlement') }}
                            </p>
                            <p
                                v-for="medium in preferences.payMediums"
                                :key="medium.value"
                                class="fs-3 lh-20 semibold mt-1 mb-0"
                            >
                                {{ medium.label }}
                            </p>
                            <p
                                v-if="!preferences.payMediums.length"
                                class="fs-3 lh-20 semibold mt-1 mb-0"
                            >
                                {{ $l10n.__('Aucun moyen de règlement spécifié') }}
                            </p>
                        </article>
                    </section>
                </div>
            </div>
        </template>
    </tab-layout>
</template>

<script>
import TabLayout from 'vcomponents/overview/tabs/TabLayout.vue';
import { getCompanyPreferences, getMetadata } from 'api/third';
import EditPreferencesThird from 'vcomponents/overview/tabs/actions/EditPreferencesThird.vue';
export default {
    components: {
        TabLayout,
        EditPreferencesThird,
    },
    props: {
        more: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            preferences: {},
            loading: false,
            metaLoading: false,
            metadata: {},
            enableEdit: false,
        };
    },
    computed: {
        buttons() {
            return this.more.buttons?.map((button) => {
                button.linkedid = this.$store.getters.linkedid;
                button.linkedtype = this.$store.getters.linkedtype;
                button.params.route = this.route;
                button.params.emitEditSuccess = this.emitEditSuccess;
                return button;
            });
        },
        isSupplier() {
            return this.$store.getters.identity.type === 'supplier';
        },
        thirdType() {
            return this.$store.getters['isCorporation'] ? 'companies' : 'individuals';
        },
        currentPaymentTerm() {
            const paymentTerm = this.metadata.paymentTerms?.find(
                (term) => term.value === this.preferences.paymentTerm.id,
            );

            return _.isNil(paymentTerm) ? [] : paymentTerm;
        },
        permissionsToEdit() {
            return this.$store?.getters?.permissions || {};
        },
    },
    created() {
        this.getPrefs();
        this.getCompanyMetadata();
    },
    methods: {
        openEdit() {
            this.enableEdit = this.permissionsToEdit?.can_edit;
        },
        getPrefs() {
            this.loading = true;
            getCompanyPreferences(this.$store.getters.linkedid, this.thirdType)
                .then((response) => {
                    this.preferences = response;
                    if (!Array.isArray(this.preferences.payMediums)) {
                        this.preferences.payMediums = Object.values(response.payMediums);
                    }
                })
                .finally(() => {
                    this.loading = false;
                    this.enableEdit = false;
                });
        },
        getCompanyMetadata() {
            this.metaLoading = true;
            getMetadata()
                .then((response) => {
                    this.metaLoading = false;
                    this.metadata = response;
                })
                .finally(() => {
                    this.metaLoading = false;
                });
        },
        hasSaved() {
            this.getPrefs();
        },
    },
};
</script>

<style lang="scss" scoped>
.well {
    gap: 16px;
}

section {
    &:last-child {
        margin-top: 2px;
    }
}

.pref-dunning:not(:last-child) {
    margin-bottom: 16px;
}
</style>
